import Bugsnag from "@bugsnag/js";
import dayjs from "dayjs";
import { isEqual } from "lodash-es";
import {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ActionCableConsumer } from "react-actioncable-provider";
import { FiHelpCircle, FiMenu, FiMoreHorizontal, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import styled, { css } from "styled-components";
import { BooleanParam, StringParam, useQueryParams } from "use-query-params";

import {
  logout,
  removeAllToasts,
  setHasNewNotification,
  setShowTooltip,
  setUnreadNotificationsCount,
  startClientTour,
  startDashboardTour,
  startNotificationsTour,
} from "../Store/Actions";
import { updateBatchActionStatus } from "../Store/Actions/analyticsNewActions";
import { setDashboardTitle } from "../Store/Actions/commonActions";
import {
  addProviderRoles,
  setIsLoadingRoles,
} from "../Store/Actions/supervisionActions";
import { TOGGLE_SIDEBAR } from "../Store/Actions/types";
import notificationBellActive from "../assets/notifications/notification-bell-active.svg";
import notificationBell from "../assets/notifications/notification-bell.svg";
import axiosInstance from "../axiosInstance";
import AttendanceModal from "../components/Dashboard/AttendanceModal";
import ChatButton from "../components/Dashboard/ChatButton";
import LinkedAccountsModal from "../components/Dashboard/EventsCalendar/LinkedAccountsModal";
import NotificationsPopup from "../components/NotificationsUpdate/NotificationsPopup";
import SidebarLink from "../components/Supervision/SidebarLink";
import AIConsentModal from "../components/common/AIConsentModal";
import AiChat from "../components/common/AiChat";
import { Button } from "../components/common/Buttons";
import { Case, Switch } from "../components/common/ConditionalRendering";
import ConfirmationPopup from "../components/common/ConfirmationPopup";
import CustomTooltip from "../components/common/CustomTooltip";
import FlexBox from "../components/common/FlexBox";
import { Body2, H2, H3, H5 } from "../components/common/Headings";
import { Modal } from "../components/common/Modal";
import {
  BRICK_TERRACOTA_200,
  BRICK_TERRACOTA_800,
  DAVYS_GREY_100,
  DAVYS_GREY_300,
  DAVYS_GREY_800,
  MOSS_GREEN_800,
  MOSS_GREEN_900,
  white,
} from "../components/common/colors";
import { SuperviseeProfileContext } from "../context/SuperviseeProfileProvider";
import { trackEvent } from "../helperFunctions";
import useFeatureFlags from "../hooks/useFeatureFlags";
import { useIdlePreload } from "../hooks/useIdlePreload";
import useMobileView from "../hooks/useMobileView";
import useNspVerification, {
  NspStatusViews,
} from "../hooks/useNspVerification";
import URL from "../urls";
import { isEmpty } from "../utils/helper";
import {
  batchStatusChannel,
  CF_PROVIDER_ROLE,
  channel,
  notificationsUnreadCountChannel,
  TYPE_PSYCHIATRIST,
} from "../variable";
import { ActionsDropdown, ProfileDropdown } from "./HeaderDropdowns";
import { fetchTwaAIUIEvents } from "../Store/Actions/twaAIActions";


export const modalImports = {
  addReport: () =>
    import("../components/Clients/ClientProfile/AllNotes/Ehr/AddReport"),
  notesModal: () =>
    import("../components/Clients/ClientProfile/AllNotes/NotesModal"),
  privateNotesModal: () =>
    import("../components/Clients/ClientProfile/AllNotes/PrivateNotesModal"),
  riskAssessmentModal: () =>
    import("../components/Clients/ClientProfile/AllNotes/RiskAssessmentModal"),
  allToolsModal: () =>
    import("../components/Clients/ClientProfile/AllTools/AllToolsModal"),
  cgiRatingModal: () =>
    import("../components/Clients/ClientProfile/CgiRating/CgiRatingModal"),
  addDiagnosisModal: () =>
    import("../components/Clients/ClientProfile/Diagnosis/AddDiagnosisModal"),
  addPrescriptionModal: () =>
    import("../components/Clients/ClientProfile/Documents/Prescriptions/AddPrescriptionModal"),
  mgafModal: () =>
    import("../components/Clients/ClientProfile/MgafRating/MgafModal"),
  generalisedNSPModal: () =>
    import("../components/Clients/ClientProfile/Sessions/GeneralizedNspModal"),
  nspConfirmationModal: () =>
    import("../components/Clients/ClientProfile/Sessions/NspConfirmationModal"),
  addConcerns: () => import("../components/Clients/ProfileHeader/AddConcerns"),
  clientDetails: () =>
    import("../components/Clients/ProfileHeader/ClientDetails"),
  updateProvider: () =>
    import("../components/Clients/ProfileHeader/UpdateProvider"),
  treatmentPlan: () => import("../components/TreatmentPlan"),
  aiSessionNoteStatesModal: () =>
    import("../components/AiAssistant/AISessionNoteStatesModal"),
  escalateRiskModal: () =>
    import("../components/Clients/ProfileHeader/EscalateRiskModal"),
};

const AddReport = lazy(modalImports.addReport);
const NotesModal = lazy(modalImports.notesModal);
const PrivateNotesModal = lazy(modalImports.privateNotesModal);
const RiskAssessmentModal = lazy(modalImports.riskAssessmentModal);
const AllToolsModal = lazy(modalImports.allToolsModal);
const CgiRatingModal = lazy(modalImports.cgiRatingModal);
const AddDiagnosisModal = lazy(modalImports.addDiagnosisModal);
const AddPrescriptionModal = lazy(modalImports.addPrescriptionModal);
const MgafModal = lazy(modalImports.mgafModal);
const GeneralisedNSPModal = lazy(modalImports.generalisedNSPModal);
const NspConfirmationModal = lazy(modalImports.nspConfirmationModal);
const AddConcerns = lazy(modalImports.addConcerns);
const ClientDetails = lazy(modalImports.clientDetails);
const UpdateProvider = lazy(modalImports.updateProvider);
const TreatmentPlan = lazy(modalImports.treatmentPlan);
const AISessionNoteStatesModal = lazy(modalImports.aiSessionNoteStatesModal);
const EscalateRiskModal = lazy(modalImports.escalateRiskModal);

const { CONFIRMATION_MODAL, NSP_MODAL } = NspStatusViews;

const Wrapper = styled(FlexBox)`
  width: 100vw;
  height: 100vh;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  a {
    text-decoration: none;
  }
`;

const SideBar = styled.div`
  height: 100%;
  width: 16.4%;
  display: grid;
  overflow: hidden;
  min-width: 14.75rem;
  grid-template-columns: 1fr;
  transition: all 300ms ease-in-out;

  ${({ expanded }) =>
    !expanded &&
    css`
      width: 0;
      min-width: 0;
      grid-template-columns: 0fr;
    `}
`;

const Logo = styled.img`
  min-width: 6rem;
  min-height: 2.5rem;
  max-width: 11rem;
`;

const SidebarContainer = styled(FlexBox)`
  width: 100%;
  height: 100%;
  row-gap: 2.5rem;
  flex-direction: column;
  justify-content: space-between;
  background-color: var(--accent-100);

  .sidebar-link {
    width: 100%;
    cursor: pointer;
    padding: 1rem 2.5rem;

    ${H3} {
      font-weight: bold;
      white-space: nowrap;
      color: var(--accent-800);
    }

    :hover {
      background-color: var(--green-100);

      ${H3} {
        color: var(--green-800);
      }
    }
  }

  .sidebar-link.active {
    background-color: var(--green-800);

    ${H3} {
      color: var(--accent-100);
    }

    :hover {
      background-color: var(--green-800);
    }
  }
`;

const AlertDiv = styled(FlexBox)`
  width: 3rem;
  height: 3rem;
  cursor: pointer;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--accent-400);
  background-color: ${white};

  svg {
    color: ${DAVYS_GREY_800};
  }

  ${({ active }) =>
    active &&
    css`
      background-color: ${MOSS_GREEN_800};

      svg {
        color: ${white};
      }
    `}
`;

const Content = styled(FlexBox)`
  flex: 1;
  height: 100%;
  overflow: auto;
  flex-direction: column;
  transition: max-width 300ms ease-in-out;
  max-width: calc(100vw - max(14.75rem, 16.4%));
  background-color: ${DAVYS_GREY_300};

  ${({ sidebarCollapsed }) =>
    sidebarCollapsed &&
    css`
      max-width: 100vw;
    `}
`;

const Header = styled(FlexBox)`
  top: 0;
  padding: 1rem;
  position: ${({ position }) => position || "sticky"};
  align-items: center;
  justify-content: space-between;
  z-index: 20;
  background-color: var(--accent-100);
`;

const TooltipBox = styled(FlexBox)`
  flex: 1;
  max-width: 23rem;
  position: relative;
  pointer-events: none;
  svg {
    pointer-events: all;
  }
`;

const SupervisionHeaderText = styled.div`
  padding: 0.25rem 1rem;
  border-radius: 0.5rem;
  background-color: ${BRICK_TERRACOTA_200};
`;

const ProviderImage = styled.img`
  min-width: 2rem;
  max-width: 2rem;
  cursor: pointer;
  min-height: 2rem;
  max-height: 2rem;
  overflow: hidden;
  object-fit: cover;
  border-radius: 50%;
  border: 1px solid var(--accent-800);
`;

/* Profile and Actions dropdowns moved to src/layout/HeaderDropdowns.jsx */
const Container = styled(FlexBox)`
  flex: 1;
  overflow: auto;
  height: 100%;
  ${({ hasFlexibleHeight }) =>
    hasFlexibleHeight &&
    css`
      height: auto;
      overflow: unset;
    `}
`;

const commonIconProps = {
  size: "1.5rem",
  cursor: "pointer",
  color: "var(--accent-800)",
};

const ModalHeader = styled(FlexBox)`
  padding: 1rem 1rem 0 1.5rem;
  justify-content: space-between;
`;

const CloseIconWrapper = styled(FlexBox)`
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const ContentWrapper = styled(FlexBox)`
  flex-direction: column;
  padding: 1.5rem;
  gap: 1.5rem;
  width: 100%;
`;

const ModalFooter = styled(FlexBox)`
  width: 100%;
  justify-content: space-between;
  align-self: flex-end;
  box-sizing: border-box;
`;

const NotificationsIconContainer = styled.div`
  position: relative;
`;

const NotificationsIcon = styled(FlexBox)`
  width: 2rem;
  height: 2rem;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const NavItem = styled.div`
  background-color: ${({ isActive }) =>
    isActive ? MOSS_GREEN_900 : "transparent"};
  ${H3} {
    color: ${({ isActive }) =>
      isActive ? DAVYS_GREY_100 : MOSS_GREEN_900} !important;
  }

  ${({ isActive }) =>
    isActive &&
    css`
      :hover {
        background: ${MOSS_GREEN_900} !important;
      }
    `}
`;

const ButtonsContainer = styled(FlexBox)`
  display: ${({ isChatOpen }) => (isChatOpen ? "none" : "flex")};
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  align-items: flex-end;
  gap: 0.75rem;
  z-index: 1000;
`;

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector(state => state.auth?.user);
  const hasUnreadNotification = useSelector(
    state => state.notifications.hasUnreadNotification
  );
  const providerProfile = useSelector(state => state.provider.providerProfile);

  const isCfProvider = providerProfile?.roles?.some(role =>
    CF_PROVIDER_ROLE.includes(role)
  );

  const showGroupOption = isCfProvider;

  const localDailySchedule = useSelector(
    state => state.provider?.dailySchedule?.localData
  );
  const remoteDailySchedule = useSelector(
    state => state.provider?.dailySchedule?.remoteData
  );

  const localBaseWeek = useSelector(
    state => state.provider?.baseWeekSchedule?.localData
  );
  const remoteBaseWeek = useSelector(
    state => state.provider?.baseWeekSchedule?.remoteData
  );

  const roles = useSelector(state => state?.supervision?.roles);
  const toasts = useSelector(state => state.toast.toasts);

  const showTooltip = useSelector(state => state.onboarding.showTooltip);
  const showLinkedAccountTooltip = useSelector(
    state => state.calendar.showLinkedAccountTooltip
  );

  const showSidebar = useSelector(state => state.commonReducer.isSidebarOpen);
  const headerTitle = useSelector(state => state.commonReducer.headerTitle);
  const isAIChatFeatureEnabled = useSelector(
    state => state.featureFlags.ai_chat_feature
  );
  const aiChatFeatureVersion = useSelector(
    state => state.featureFlags.ai_chat_feature_version
  );
  const isTwaMasterAIConsentEnabled = useSelector(
    state => state.featureFlags.twa_master_ai_consent
  );

  const reduxChatList = useSelector(state => state?.chat?.chatList);

  const { addToast } = useToasts();

  const [queryParams, setQueryParams] = useQueryParams({
    showClientDetailsModal: BooleanParam,
    showNotesModal: BooleanParam,
    showPrivateNotesModal: BooleanParam,
    showAddPrescriptionModal: BooleanParam,
    showAddDiagnosisModal: BooleanParam,
    showNspModal: BooleanParam,
    showUpdateProviderModal: BooleanParam,
    showAddConcernsModal: BooleanParam,
    showAllToolsModal: BooleanParam,
    showAddEhrModal: BooleanParam,
    showMgafModal: BooleanParam,
    showCgiModal: BooleanParam,
    showLinkedAccountsModal: BooleanParam,
    showAttendanceModal: BooleanParam,
    showTreatmentPlanModal: BooleanParam,
    showAIConsentModal: BooleanParam,
    showRiskAssessmentModal: BooleanParam,
    showEscalateRiskModal: BooleanParam,
    currentView: StringParam,
    showChatList: BooleanParam,
  });

  const {
    showClientDetailsModal,
    showNotesModal,
    showPrivateNotesModal,
    showAddEhrModal,
    showAddPrescriptionModal,
    showAddDiagnosisModal,
    showUpdateProviderModal,
    showAddConcernsModal,
    showAllToolsModal,
    showMgafModal,
    showCgiModal,
    showLinkedAccountsModal,
    showAttendanceModal,
    showTreatmentPlanModal,
    showAIConsentModal,
    showRiskAssessmentModal,
    showEscalateRiskModal,
    currentView,
  } = queryParams;
  
  const { isSupervisionMode, superviseeProfile } = useContext(
    SuperviseeProfileContext
  );
  const superviseeName = superviseeProfile?.name;

  const userType = user?.usertype;
  const userName = `${user?.firstname || ""} ${user?.lastname || ""}`;

  const isHelpAndUpdatesPage = location?.pathname?.includes("help-and-updates");
  const isNotificationsPage = location?.pathname?.includes("notifications");
  const isAnalyticsPage = location?.pathname?.includes("analytics");
  const isSessionsPage = location.pathname?.includes("sessions");
  const isDashboardPage = location.pathname?.includes("dashboard");
  const isClientsPage =
    location.pathname?.includes("clients") && !isAnalyticsPage;
  const isSupervisionPage = location.pathname?.includes("supervision");
  const isGroupsPage = location.pathname?.includes("groups");
  const isAvailabilityPage = location.pathname?.includes("availability");
  const isCoachCallPage = location.pathname?.includes("coach-call");
  const isCoachRole = providerProfile?.roles?.includes("coach");

  const isAnalyticsHomePage = /^\/analytics\/?$/.test(location.pathname);

  const commonAnalyticsPayload = {
    provider_name: userName,
    service_type: userType === TYPE_PSYCHIATRIST ? "psychiatry" : "therapy",
  };

  const [showNotificationsPopup, setShowNotificationsPopup] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showActionsDropdown, setShowActionsDropdown] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  const [showCloseModalConfirmation, setShowCloseModalConfirmation] =
    useState(false);
  const [redirectLink, setRedirectLink] = useState("");

  const navigate = useNavigate();
  const isMobile = useMobileView();
  const notificationsPopupRef = useRef(null);
  const isChatOpen = useSelector(state => state.chat?.isChatOpen);

  const isCoupleTherapist =
    providerProfile?.is_couples_therapist ||
    providerProfile?.roles?.includes("couple-therapist");
  const [
    isDashboardListingEnabled,
    isCalendarEnabled,
    isNotificationsEnabled,
    isAvailabilityEnabled,
    isSupervisionEnabled,
    isAnalyticsEnabled,
    isChatEnabled,
  ] = useFeatureFlags([
    "twa_private_session_listing_dashboard",
    "twa_private_google_calender_auth",
    "twa_private_notifications",
    "twa_private_availability",
    "twa_private_supervision",
    "twa_private_analytics",
    "twa_private_chat_clients",
  ]);

  const { activeNspModal, setActiveNspModal } = useNspVerification({
    isCoupleTherapist,
  });

  let actionCable;

  const showTourEntryPoint =
    !isSupervisionPage &&
    (isDashboardPage || isClientsPage || isNotificationsPage) &&
    !isCfProvider;

  const isDashboardRouteEnabled =
    isDashboardListingEnabled || isCalendarEnabled;

  const importsToPreload = useMemo(
    () => [
      modalImports.addReport,
      modalImports.notesModal,
      modalImports.privateNotesModal,
      modalImports.riskAssessmentModal,
      modalImports.allToolsModal,
      modalImports.cgiRatingModal,
      modalImports.addDiagnosisModal,
      modalImports.addPrescriptionModal,
      modalImports.mgafModal,
      modalImports.generalisedNSPModal,
      modalImports.nspConfirmationModal,
      modalImports.addConcerns,
      modalImports.clientDetails,
      modalImports.updateProvider,
      modalImports.treatmentPlan,
      modalImports.escalateRiskModal,
    ],
    []
  );

  useIdlePreload(importsToPreload);

  useEffect(() => {
    dispatch(fetchTwaAIUIEvents());
  }, []);

  useEffect(() => {
    if (isAnalyticsPage) dispatch(setDashboardTitle("Analytics"));
  }, [isAnalyticsPage, dispatch]);

  useEffect(() => {
    dispatch(setShowTooltip(false));
  }, [location]);

  useEffect(() => {
    let timeout;
    if (showTooltip) {
      timeout = setTimeout(() => {
        dispatch(setShowTooltip(false));
      }, 3000);
    }
    return () => clearTimeout(timeout);
  }, [showTooltip]);

  useEffect(() => {
    dispatch(removeAllToasts());

    toasts.map(toast =>
      addToast(toast.msg, {
        appearance: toast.appearance || "success",
        autoDismiss: toast.autoDismiss || true,
        autoDismissTimeout: toast.autoDismissTimeout
          ? toast.autoDismissTimeout
          : 5000,
        showUndoBtn: toast.showUndoBtn || false,
        onUndoClick: toast.onUndoClick,
      })
    );
  }, [toasts, addToast, dispatch]);

  useEffect(() => {
    if (!user?.uuid) return;
    const fetchProviderRoles = async () => {
      dispatch(setIsLoadingRoles(true));
      try {
        dispatch(
          addProviderRoles(
            (await axiosInstance.get(URL.getProvidersRoles))?.data?.data || []
          )
        );
      } catch (err) {
        Bugsnag.notify(err);
      } finally {
        dispatch(setIsLoadingRoles(false));
      }
    };
    fetchProviderRoles();
  }, [user?.uuid, dispatch]);

  useEffect(() => {
    if (!user) return;
    const aiPermissionModalShown = localStorage.getItem(
      "aiPermissionModalShown"
    );
    if (aiPermissionModalShown === "true") return;
    setQueryParams(
      { showAIConsentModal: 1, aiConsentSource: "dashboard" },
      "replaceIn"
    );
    trackEvent({
      event: "twa_ai_permission_modal_shown",
      payload: { source: "dashboard" },
    });
  }, [user, providerProfile]);

  const toggleSidebar = () => dispatch({ type: TOGGLE_SIDEBAR });

  const toggleProfileDropdown = () => setShowProfileDropdown(prev => !prev);

  const toggleActionsDropdown = () => setShowActionsDropdown(prev => !prev);

  const handleTabClick = event => {
    const { id, uuid, name, usertype } = user || {};

    trackEvent({
      event: event,
      payload: {
        provider_id: id,
        provider_name: name,
        provider_uuid: uuid,
        provider_type: usertype,
        is_cf_provider: isCfProvider,
      },
    });
  };

  const handleNavigation = (page, link) => {
    if (
      (currentView === "singleday" &&
        !isEqual(localDailySchedule, remoteDailySchedule)) ||
      (currentView === "baseweek" && !isEqual(localBaseWeek, remoteBaseWeek))
    ) {
      setShowCloseModalConfirmation(true);
      setRedirectLink(link);
    } else {
      navigate(link);
    }
    fireTabChangeEvent(page);
  };

  const closeModal = () => {
    setShowCloseModalConfirmation(false);
    navigate(redirectLink);
  };

  const fireTabChangeEvent = new_tab => {
    trackEvent({
      event: "twa_tab_change",
      payload: { current_tab: getCurrentTabName(), new_tab },
    });
  };

  const getCurrentTabName = () => {
    if (isDashboardPage) return "dashboard";
    if (isClientsPage) return "clients";
    if (isAnalyticsPage) return "analytics";
    if (isSupervisionPage) return "supervision";
    if (isNotificationsPage) return "notifications";
    return "availability";
  };

  const handleNotificationClick = () => {
    if (!showNotificationsPopup) {
      trackEvent({
        event: "notification_bell_click",
        payload: commonAnalyticsPayload,
      });
    }
    setShowNotificationsPopup(!showNotificationsPopup);
  };

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleTour = () => {
    if (isSessionsPage) {
      navigate(`/dashboard?date=${dayjs(new Date()).format("YYYY-MM-DD")}`);
      dispatch(startDashboardTour(true));
    }
    if (isDashboardPage) {
      dispatch(startDashboardTour(true));
      if (!isChatOpen) {
        dispatch({ type: "TOGGLE_CHAT_BLOCK" });
      }
    } else if (isClientsPage) {
      dispatch(startClientTour(true));
    } else if (isNotificationsPage) {
      dispatch(startNotificationsTour(true));
    }
  };

  const handleBatchActionStatusUpdate = data => {
    const {
      provider_id,
      type,
      message,
      data: { record_id, success, failure } = {},
    } = data || {};

    if (provider_id !== user?.id) return;

    addToast(message, {
      appearance: type,
      autoDismiss: true,
    });

    dispatch(
      updateBatchActionStatus({
        type,
        recordId: record_id,
        successCount: success,
        failedCount: failure,
      })
    );
  };

  const newChatUnreadRoomCount = useSelector(
    state => state.chat?.newChatUnreadRoomIds?.length || 0
  );
  const hasUnreadMessages = providerProfile?.new_chat_enabled
    ? newChatUnreadRoomCount > 0
    : reduxChatList?.some(chat => chat.client && chat.client.unreadCount > 0);

  const showChatButton = !isMobile && isChatEnabled && isDashboardPage;
  const showAiChatButton =
    isAIChatFeatureEnabled && aiChatFeatureVersion === "v3";
  const shouldShowAIConsentModal =
    isTwaMasterAIConsentEnabled && !!showAIConsentModal;
  return (
    <>
      <Suspense fallback={null}>
        {showCloseModalConfirmation && (
          <ConfirmationPopup
            title="Are you sure?"
            subtitle="You haven't saved your changes, are you sure you want to leave this page?"
            cancelTitle="NO, GO BACK"
            confirmTitle="YES, CLOSE"
            showConfirmAsDanger={true}
            onCancel={() => setShowCloseModalConfirmation(false)}
            onConfirm={closeModal}
          />
        )}
        {showAddPrescriptionModal && <AddPrescriptionModal />}
        {showAddDiagnosisModal && <AddDiagnosisModal />}
        {showClientDetailsModal && <ClientDetails />}
        {showNotesModal && <NotesModal />}
        {showPrivateNotesModal && <PrivateNotesModal />}
        {showAddEhrModal && <AddReport />}
        {showUpdateProviderModal && <UpdateProvider />}
        {showAddConcernsModal && <AddConcerns />}
        {showAllToolsModal && <AllToolsModal />}
        {showMgafModal && <MgafModal />}
        {showCgiModal && <CgiRatingModal />}
        {showLinkedAccountsModal && <LinkedAccountsModal />}
        {showTreatmentPlanModal && <TreatmentPlan />}
        {shouldShowAIConsentModal && <AIConsentModal />}
        {showRiskAssessmentModal && <RiskAssessmentModal />}
        {showEscalateRiskModal && <EscalateRiskModal />}
        <AISessionNoteStatesModal
          isAiModalViewed={providerProfile?.is_ai_modal_viewed}
        />
        <Switch>
          <Case condition={activeNspModal === CONFIRMATION_MODAL}>
            <NspConfirmationModal setActiveNspModal={setActiveNspModal} />
          </Case>
          <Case condition={activeNspModal === NSP_MODAL}>
            <GeneralisedNSPModal />
          </Case>
        </Switch>
      </Suspense>
      {showLogoutModal && (
        <Modal
          borderRadius="1rem"
          xs
          maxWidth={"27rem"}
          height="fit-content"
          id="header-user-image"
        >
          <ModalHeader>
            <H3 bold>Are you sure you want to log out?</H3>
            <CloseIconWrapper onClick={() => setShowLogoutModal(false)}>
              <FiX />
            </CloseIconWrapper>
          </ModalHeader>
          <ContentWrapper>
            <H5>
              Once you log out, you will need to use your Amaha Professional
              credentials to log in again.
            </H5>

            <ModalFooter>
              <Button
                id="logout-cancel-button"
                outline
                secondary
                onClick={() => setShowLogoutModal(false)}
              >
                CANCEL
              </Button>
              <Button primary id="confirm-logout-button" onClick={handleLogout}>
                LOG OUT
              </Button>
            </ModalFooter>
          </ContentWrapper>
        </Modal>
      )}
      {!isSupervisionMode &&
        !isCfProvider &&
        isNotificationsEnabled &&
        (!!actionCable ||
          (actionCable = (
            <>
              <ActionCableConsumer
                channel={{ channel: channel }}
                onReceived={({ data }) => {
                  if (data?.user_id === user?.id) {
                    dispatch(setHasNewNotification(true));
                  }
                }}
              />
              <ActionCableConsumer
                channel={{ channel: notificationsUnreadCountChannel }}
                onReceived={({ data }) => {
                  dispatch(setUnreadNotificationsCount(data));
                }}
              />
              <ActionCableConsumer
                channel={{ channel: batchStatusChannel }}
                onReceived={handleBatchActionStatusUpdate}
              />
            </>
          )))}
      {showAttendanceModal && <AttendanceModal />}
      <Wrapper>
        <SideBar expanded={showSidebar}>
          <SidebarContainer>
            <FlexBox column rowGap="2.5rem">
              <FlexBox padding="2.5rem">
                <Logo
                  src={
                    "https://assets.theinnerhour.com/compressed/Logo_1776244706601_m3n4ity.webp"
                  }
                  alt="Amaha logo"
                  draggable="false"
                />
              </FlexBox>

              <FlexBox column>
                {isDashboardRouteEnabled && (
                  <SidebarLink>
                    <NavItem
                      id="sidebar-dashboard"
                      isActive={isDashboardPage}
                      className="sidebar-link"
                      onClick={() =>
                        handleNavigation("dashboard", "/dashboard")
                      }
                    >
                      <H3>Dashboard</H3>
                    </NavItem>
                  </SidebarLink>
                )}

                <NavItem
                  id="sidebar-clients"
                  isActive={isClientsPage}
                  className="sidebar-link"
                  onClick={
                    isSupervisionMode
                      ? undefined
                      : () => handleNavigation("clients", "/clients")
                  }
                >
                  <H3>Clients</H3>
                </NavItem>

                {showGroupOption && (
                  <NavItem
                    id="sidebar-groups"
                    isActive={isGroupsPage}
                    className="sidebar-link"
                    onClick={() => handleNavigation("groups", "/groups")}
                  >
                    <H3>Groups</H3>
                  </NavItem>
                )}
                {/* Analytics Tab hidden for CF provider */}
                {!isCfProvider && isAnalyticsEnabled && (
                  <SidebarLink>
                    <NavItem
                      id="sidebar-analytics"
                      isActive={isAnalyticsPage}
                      className="sidebar-link"
                      onClick={() => {
                        handleTabClick("twa_analytics_click");
                        handleNavigation("analytics", "/analytics");
                      }}
                    >
                      <H3>Analytics</H3>
                    </NavItem>
                  </SidebarLink>
                )}

                {isAvailabilityEnabled && (
                  <SidebarLink>
                    <NavItem
                      id="sidebar-availability"
                      isActive={isAvailabilityPage}
                      className="sidebar-link"
                      onClick={() =>
                        handleNavigation("availability", "/availability")
                      }
                    >
                      <H3>Availability</H3>
                    </NavItem>
                  </SidebarLink>
                )}
                {!!roles?.length && isSupervisionEnabled && (
                  <SidebarLink>
                    <NavItem
                      id="sidebar-supervision"
                      isActive={isSupervisionPage}
                      className="sidebar-link"
                      onClick={() => {
                        handleTabClick("twa_supervision_click");
                        handleNavigation("supervision", "/supervision");
                      }}
                    >
                      <H3>Supervision</H3>
                    </NavItem>
                  </SidebarLink>
                )}
                {isNotificationsEnabled && !isCfProvider && (
                  <SidebarLink>
                    <NavItem
                      id="sidebar-notifications"
                      isActive={isNotificationsPage}
                      className="sidebar-link"
                      onClick={() =>
                        handleNavigation("notifications", "/notifications")
                      }
                    >
                      <H3>Notifications</H3>
                    </NavItem>
                  </SidebarLink>
                )}
                {isCoachRole && !isCfProvider && !isMobile && (
                  <SidebarLink>
                    <NavItem
                      id="sidebar-coach-call"
                      isActive={isCoachCallPage}
                      className="sidebar-link"
                      onClick={() =>
                        handleNavigation("coach-call", "/coach-call")
                      }
                    >
                      <H3>Coach Call</H3>
                    </NavItem>
                  </SidebarLink>
                )}
              </FlexBox>
            </FlexBox>

            <FlexBox padding="2.5rem">
              <AlertDiv
                active={isHelpAndUpdatesPage}
                onClick={() => navigate("/help-and-updates")}
              >
                <FiHelpCircle size="1.5rem" />
              </AlertDiv>
            </FlexBox>
          </SidebarContainer>
        </SideBar>

        <Content sidebarCollapsed={!showSidebar} className="hide-scrollbar">
          {isSupervisionMode ? (
            <Header>
              <FlexBox align="center" columnGap="1.5rem">
                <FiMenu onClick={toggleSidebar} {...commonIconProps} />
                <H2 bold>{superviseeName && `${superviseeName}'s Clients`}</H2>
              </FlexBox>
              <SupervisionHeaderText>
                <Body2 bold color={BRICK_TERRACOTA_800}>
                  Supervision Mode
                </Body2>
              </SupervisionHeaderText>
            </Header>
          ) : (
            <Header position={isAnalyticsHomePage ? "static" : "sticky"}>
              <FlexBox align="center" columnGap="1.5rem">
                <FiMenu onClick={toggleSidebar} {...commonIconProps} />
                <FlexBox align="center" columnGap="0.5rem">
                  <H2 bold>{headerTitle}</H2>
                  {showTourEntryPoint && (
                    <TooltipBox>
                      <FiHelpCircle
                        {...commonIconProps}
                        size="1.25rem"
                        strokeWidth={2.5}
                        onClick={handleTour}
                        id="help-circle"
                      />
                      {showTooltip && (
                        <CustomTooltip
                          text="Click here to learn more about this page."
                          place="bottom"
                        />
                      )}
                    </TooltipBox>
                  )}
                </FlexBox>
              </FlexBox>

              <FlexBox align="center" columnGap="1.5rem">
                {!isCfProvider && isNotificationsEnabled && (
                  <NotificationsIconContainer ref={notificationsPopupRef}>
                    <NotificationsIcon onClick={handleNotificationClick}>
                      <img
                        src={
                          hasUnreadNotification
                            ? notificationBellActive
                            : notificationBell
                        }
                        className="bell-icon"
                        alt="notification icon"
                      />
                    </NotificationsIcon>
                    {showNotificationsPopup && (
                      <NotificationsPopup
                        containerRef={notificationsPopupRef}
                        setShowNotificationsPopup={setShowNotificationsPopup}
                        commonAnalyticsPayload={commonAnalyticsPayload}
                      />
                    )}
                  </NotificationsIconContainer>
                )}

                <FlexBox position="relative" align="center" justify="center">
                  <ProviderImage
                    alt="User Image"
                    draggable="false"
                    id="header-user-image"
                    src={"https:" + user?.image}
                    onClick={toggleProfileDropdown}
                  />

                  {showProfileDropdown && (
                    <ProfileDropdown
                      user={user}
                      userName={userName}
                      userType={userType}
                      logout={() => dispatch(logout)}
                      supervisionMode={isSupervisionMode}
                      toggleDropdown={toggleProfileDropdown}
                      toggleLogoutModal={() => setShowLogoutModal(true)}
                    />
                  )}
                </FlexBox>

                <FlexBox position="relative" align="center" justify="center">
                  <FlexBox
                    cursor="pointer"
                    id="header-actions"
                    onClick={toggleActionsDropdown}
                  >
                    <TooltipBox>
                      <FiMoreHorizontal
                        {...commonIconProps}
                        pointerEvents="none"
                      />
                      {showLinkedAccountTooltip && (
                        <CustomTooltip
                          text="You can access this later here"
                          place="bottom"
                          offset
                        />
                      )}
                    </TooltipBox>
                  </FlexBox>

                  {showActionsDropdown && (
                    <ActionsDropdown toggleDropdown={toggleActionsDropdown} />
                  )}
                </FlexBox>
              </FlexBox>
            </Header>
          )}

          <Container hasFlexibleHeight={isAnalyticsHomePage}>
            {children}
            <ButtonsContainer isChatOpen={isChatOpen}>
              {showChatButton && (
                <ChatButton hasUnreadMessages={hasUnreadMessages} />
              )}
            </ButtonsContainer>
            {showAiChatButton && <AiChat />}
          </Container>
        </Content>
      </Wrapper>
    </>
  );
};

export default DashboardLayout;
